import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, CircularProgress, Checkbox, Paper } from '@mui/material';
import { Text } from '@legacy/components/ui-components';
import {
  DataGrid,
  Tooltip,
  ToastifyAlert,
} from '@legacy/components/mui-components';
import {
  GridColDef,
  GridRowParams,
  GridRenderCellParams,
  DataGridProProps,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
} from '@mui/x-data-grid-pro';
import { toast } from 'react-toastify';
import { useGetAddedValuePerMonthHoursOnlyReportingWidget } from '@legacy/apis/reportingWidgetsAPI';
import { useGetWidgetsRoles, usePutWidgetRole } from '@legacy/apis/widgetsAPI';
import elements from '@legacy/assets/styles/variables/colors/global/elements.module.scss';
import { translationAnyText } from '@/legacy/utils/translation';

// Data type definitions
interface IRole {
  id: number;
  roleName: string;
}

interface IWidget {
  id: number;
  widgetName: string;
  rolePermissions: number[];
}

// Helper: Remove "Widget" prefix from widget name
function getWidgetTranslationBaseKey(widgetName: string): string {
  return widgetName.startsWith('Widget')
    ? widgetName.replace(/^Widget/, '')
    : widgetName;
}

const WidgetsRoles: React.FC = () => {
  const [detailPanelWidth, setDetailPanelWidth] = useState(0);
  const { currencyAbb } = useGetAddedValuePerMonthHoursOnlyReportingWidget();
  const { data, isLoading, error } = useGetWidgetsRoles();
  const { t: tWidgets } = useTranslation('reportingWidgets');
  const { t: tFrontPage } = useTranslation('frontpage');
  const updateMutation = usePutWidgetRole();

  // Let detail panel auto-size
  const getDetailPanelHeight = useCallback<
    NonNullable<DataGridProProps['getDetailPanelHeight']>
  >(() => 'auto' as const, []);

  // Manage widgetPermissions in local state
  const [widgetPermissions, setWidgetPermissions] = useState<
    Record<number, number[]>
  >({});

  // Track pending mutations by widget/role to handle concurrent edits
  const pendingMutationsRef = useRef<Record<string, boolean>>({});

  // Example dynamic width calculation (if you need to subtract a sidebar)
  useEffect(() => {
    function updateWidth() {
      // This is a magic number, might need to redo this part
      setDetailPanelWidth(window.innerWidth - 480);
    }
    window.addEventListener('resize', updateWidth);
    updateWidth();
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Initialize widgetPermissions after data is loaded
  useEffect(() => {
    if (data) {
      const { widgets = [] } = data;
      const initialPermissions: Record<number, number[]> = {};
      widgets.forEach((widget: IWidget) => {
        initialPermissions[widget.id] = widget.rolePermissions || [];
      });
      setWidgetPermissions(initialPermissions);
    }
  }, [data]);

  // Handle toggling a role's permission for a widget
  const handleCheckboxChange = (widgetId: number, roleId: number) => {
    const currentRoles = widgetPermissions[widgetId] || [];
    const isChecked = currentRoles.includes(roleId);
    const updatedRoles = isChecked
      ? currentRoles.filter((id) => id !== roleId)
      : [...currentRoles, roleId];

    // Apply optimistic update
    setWidgetPermissions((prev) => ({
      ...prev,
      [widgetId]: updatedRoles,
    }));

    // Create a unique key for this widget/role combination
    const mutationKey = `${widgetId}-${roleId}`;

    // If there's already a pending mutation for this widget/role, ignore it
    // Our optimistic update already reflects the newest intended state
    if (pendingMutationsRef.current[mutationKey]) {
      return;
    }

    // Mark this mutation as pending
    pendingMutationsRef.current[mutationKey] = true;

    updateMutation.mutate(
      { widgetId: widgetId.toString(), roleId, enabled: !isChecked },
      {
        onSuccess: () => {
          // Clear the pending mutation flag
          delete pendingMutationsRef.current[mutationKey];

          toast.success(
            <ToastifyAlert
              description={tWidgets('ChangesSuccessfullySaved')}
            />,
            {
              autoClose: 5000,
              closeButton: false,
            },
          );
        },
        onError: () => {
          // Clear the pending mutation flag
          delete pendingMutationsRef.current[mutationKey];

          // Get the current state to determine if we need to roll back
          setWidgetPermissions((prev) => {
            // Only roll back if the widget state still matches our optimistic update
            // This prevents overwriting newer changes
            if (
              JSON.stringify(prev[widgetId]) === JSON.stringify(updatedRoles)
            ) {
              return {
                ...prev,
                [widgetId]: currentRoles,
              };
            }
            // Otherwise, keep the current state
            return prev;
          });

          toast.error(
            <ToastifyAlert description={tWidgets('FailedToSaveChanges')} />,
            {
              autoClose: 5000,
              closeButton: false,
            },
          );
        },
      },
    );
  };

  // Loading/error states
  if (isLoading) {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Text>{tFrontPage('MilestonesErrorMessage')}</Text>;
  }
  if (!data) {
    return null;
  }

  const { roles = [], widgets = [] } = data;

  // Build role columns
  const roleColumns: GridColDef[] = roles.map((role: IRole) => ({
    field: `role_${role.id}`,
    headerName: role.roleName,
    width: 160,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: GridRenderCellParams) => {
      const row = params.row as IWidget & { translatedWidgetName: string };
      const widgetId = row.id;
      const checked = widgetPermissions[widgetId]?.includes(role.id) || false;

      return (
        <Checkbox
          checked={checked}
          onChange={(event) => {
            event.stopPropagation();
            handleCheckboxChange(widgetId, role.id);
          }}
          onKeyDown={(event) => event.stopPropagation()}
          color="primary"
          slotProps={{
            input: {
              'aria-label': `${row.translatedWidgetName} for ${role.roleName}`,
            },
          }}
        />
      );
    },
  }));

  // Main columns
  const columns: GridColDef[] = [
    {
      field: 'translatedWidgetName',
      headerName: tWidgets('WidgetRolesTableNameColumn'),
      width: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || ''}>
          <Box
            sx={{
              width: '180px',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              whiteSpace: 'normal',
              lineHeight: '1.3em',
              maxHeight: '3em',
              pr: 2,
            }}
          >
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    ...roleColumns,
  ];
  // Create rows
  const rows = widgets.map((widget: IWidget) => {
    const baseKey = getWidgetTranslationBaseKey(widget.widgetName);
    return {
      ...widget,
      id: widget.id,
      translatedWidgetName: translationAnyText(tWidgets, baseKey),
    };
  });

  // Detail panel content
  const getDetailPanelContent = (params: GridRowParams<IWidget>) => {
    const { row } = params;
    const baseKey = getWidgetTranslationBaseKey(row.widgetName);
    const descriptionKey = `${baseKey}WidgetDescription` as any;

    return (
      <Box
        sx={{
          height: '100%',
          width: `${detailPanelWidth}px !important`, // or remove if not needed
          overflowY: 'auto',
          borderTop: `1px solid ${elements.colorBorder}`,
          py: 2,
        }}
      >
        <Paper sx={{ mx: 'auto', width: '90%', p: 1 }}>
          <Trans
            i18nKey={descriptionKey}
            ns="reportingWidgets"
            components={{ 1: <br /> }}
            values={{ currency: currencyAbb?.toString() }}
          />
        </Paper>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        // Pinned columns: using the constant for detail panel toggle
        // plus your "translatedWidgetName" column
        pinnedColumns={{
          left: [GRID_DETAIL_PANEL_TOGGLE_FIELD, 'translatedWidgetName'],
        }}
        disableColumnMenu
        disableColumnTopBorder
        disableRowSelectionOnClick
        disableColumnResize
        autoHeight
        rows={rows}
        columns={columns}
        loading={false}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        pagination
        hideFooter
        pageSizeOptions={[20, 50, 100]}
        initialState={{
          density: 'standard',
          pagination: {
            paginationModel: {
              pageSize: 20,
            },
          },
        }}
        sx={{
          border: `1px solid ${elements.colorBorder}`,
          '& .MuiDataGrid-columnHeaders': {
            minHeight: 'auto',
            maxHeight: 'none',
            lineHeight: '1.3em',
            overflow: 'visible',
            wordWrap: 'break-word',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            whiteSpace: 'normal',
            display: 'block',
            textAlign: 'center',
            wordBreak: 'break-word',
            fontSize: '13px',
            paddingY: '5px',
          },
          '& .MuiDataGrid-columnHeader': {
            height: 'auto !important',
          },
        }}
      />
    </Box>
  );
};

export { WidgetsRoles };
